import { PureComponent } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import GifPropType from 'shared/prop-types/gif'
import { withRouter } from 'react-router'
import { Gif } from '@giphy/react-giphy-brand'
import { sanitize } from 'dompurify'
import { trendingCarouselPingBackEvent as firePingback } from 'shared/pingbacks/pingback-networking'
import { debounce } from 'lodash'
import { googleAnalyticsEvent } from 'shared/util/analytics'

const ListWrapper = styled.div`
    height: ${props => props.height}px;
    margin: 0 0 ${({ marginBottom }) => marginBottom}px 0;
    overflow: hidden;
    width: 100%;
`

const List = styled.ul`
    -webkit-overflow-scrolling: touch;
    overflow-x: scroll;
    overflow-y: hidden;
    margin: 0;
    padding: 0 6px 0 0;
    white-space: nowrap;
    text-align: left;
    height: ${props => props.height + 20}px;
`

const Item = styled.li`
    display: inline-block;
    margin-left: 6px;
    list-style: none;
    position: relative;
    vertical-align: top;
    z-index: 1;
    &:first-of-type {
        margin-left: 0px;
    }
`

const getItemDim = (gif, setWidth, setHeight, fallback = 160) => {
    if (gif && gif.images && gif.images.original) {
        const {
            images: {
                original: { width, height },
            },
        } = gif
        if (setHeight && !setWidth) {
            return (width * setHeight) / height
        }
    }
    return fallback
}

@withRouter
export default class ScrollCarousel extends PureComponent {
    static propTypes = {
        gifs: PropTypes.arrayOf(GifPropType),
        width: PropTypes.number,
        height: PropTypes.number,
        rendition: PropTypes.string,
        showTags: PropTypes.bool,
        hideLock: PropTypes.bool,
        onClick: PropTypes.func,
        onScroll: PropTypes.func,
        saveRef: PropTypes.func,
        isTracking: PropTypes.bool,
        marginBottom: PropTypes.number,
        isInDropdown: PropTypes.bool,
    }
    state = {
        seenGifIds: [],
    }
    loadedGifs = []
    componentDidMount() {
        const { saveRef } = this.props
        saveRef && saveRef(this.carousel) // pass el up to parent
    }
    componentDidUpdate(prevProps) {
        const { gifs } = this.props
        if (!prevProps.gifs.length && gifs.length) {
            this.setState({ gifsNotSeen: [...gifs] })
        }
    }
    carouselScroll(e) {
        const { onScroll } = this.props
        onScroll && onScroll(e) // parent action
        this.checkForSeenGifs()
    }
    onHover(gif, index) {
        const { responseId, isTracking } = this.props
        isTracking && this.formatPingback(gif, responseId, `HOVER`, index)
    }
    onClick(e, gif, index) {
        const { responseId, isTracking, isInDropdown } = this.props
        if (isInDropdown) {
            googleAnalyticsEvent({ action: 'Click', category: 'Search', label: 'Trending Carousel' })
        }

        isTracking && this.formatPingback(gif, responseId, `CLICK`, index, e)
    }
    thirdPartyTracking({ tags = [] }) {
        tags.forEach(tag => {
            const el = document.createElement('html')
            tag = tag.replace('%%CACHEBUSTER%%', Date.now())
            el.innerHTML = sanitize(tag)
            const pixel = el.querySelector('img')
            if (pixel) {
                document.querySelector('head').appendChild(pixel)
            }
        })
    }
    formatPingback(gif, responseId, actionType, position, e) {
        const { id, bottle_data = {} } = gif
        const { user, onClick } = this.props
        const { tid } = bottle_data
        const action = this.createPingbackAction(actionType, id, tid, position)
        firePingback(action, responseId, user, !!tid)
        if (e) {
            onClick(e, gif)
        }
    }
    createPingbackAction(actionType, gifId, tid, position) {
        const value = position.toString()
        const layoutPingback = { key: 'layout_type', value: this.props.isInDropdown ? 'CAROUSEL_SEARCH' : 'CAROUSEL' }
        return {
            action_type: actionType,
            ts: new Date().getTime(),
            gif_id: gifId,
            tid: tid,
            attributes: [
                layoutPingback,
                {
                    key: `position`,
                    value: value, // zero-indexed isGucci
                },
            ],
        }
    }
    checkForSeenGifs = debounce(() => {
        // iterate through all gifs that have been loaded
        // but not seen yet, and make sure they are fully
        // visible in the viewport before considering
        // them as seen.
        const { gifs, responseId } = this.props
        const { seenGifIds } = this.state
        const newSeenIds = []
        const { left: boundsLeft, right: boundsRight } = this.carousel.getBoundingClientRect()
        this.loadedGifs.forEach(({ gifId, img }) => {
            if (seenGifIds.indexOf(gifId) < 0) {
                const { left, top, bottom, right } = img.getBoundingClientRect()
                if (left >= boundsLeft && top >= 0 && bottom <= window.innerHeight && right <= boundsRight) {
                    newSeenIds.push(gifId)

                    const gif = gifs.find(gif => gif.id === gifId)
                    const { bottle_data } = gif

                    // Third party tracking
                    if (bottle_data) {
                        this.thirdPartyTracking(bottle_data)
                    }

                    // First party pingback tracking
                    this.formatPingback(gif, responseId, `SEEN`, gifs.indexOf(gif))
                }
            }
        })
        if (newSeenIds.length) {
            this.loadedGifs = this.loadedGifs.filter(item => newSeenIds.indexOf(item.gifId) < 0)
            this.setState({ seenGifIds: [...seenGifIds, ...newSeenIds] })
        }
    }, 300)
    onGifVisible = ({ id: gifId }, img) => {
        const { seenGifIds } = this.state
        // if this gif has not triggered its onSeen
        // callback yet, add it to the loadedGifImages
        // dict and call checkForSeenGifs
        if (seenGifIds.indexOf(gifId) < 0) {
            this.loadedGifs.push({ gifId, img })
            this.checkForSeenGifs()
        }
    }
    render() {
        const { gifs, autoPlay, height = 100, rendition, showTags, hideLock, marginBottom } = this.props
        return (
            <ListWrapper height={height} marginBottom={marginBottom}>
                <List ref={c => (this.carousel = c)} onScroll={e => this.carouselScroll(e)} height={height}>
                    {gifs.map((gif, index) => {
                        if (gif) {
                            const { id } = gif
                            const width = getItemDim(gif, undefined, height)
                            return (
                                <Item key={index} onMouseDown={e => this.onClick(e, gif, index)}>
                                    <Gif
                                        key={id}
                                        gif={gif}
                                        width={width}
                                        height={height}
                                        autoPlay={autoPlay}
                                        rendition={rendition}
                                        showTags={showTags}
                                        hideLock={hideLock}
                                        onClick={e => this.onClick(e, gif, index)}
                                        onHover={() => this.onHover(gif, index)}
                                        onVisible={this.onGifVisible}
                                    />
                                </Item>
                            )
                        }
                    })}
                </List>
            </ListWrapper>
        )
    }
}
